import React, { useState } from 'react';
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Form, Row, Col, Table, Button, Badge, Modal, Nav, Tab } from "react-bootstrap";
import { FilterOutlined } from '@ant-design/icons';

import PageTitle from "@/common/PageTitle/PageTitle";
import DateRangePicker from "@/common/DateRangePicker/DateRangePicker";
import styles from "./pros-location-reports.module.scss";

interface LocationRecord {
  id: string;
  brand: string;
  address: string;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  quarterStartTotal: number;
  monthEndTotal: number;
  removedInMonth?: string;
  notes?: string;
  quarter: string;
  month: string;
  year: string;
  createdAt: string;
  updatedAt: string;
}

interface MonthlySummary {
  month: string;
  year: string;
  quarter: string;
  totalLocations: number;
  removedLocations: number;
  removedLocationDetails: LocationRecord[];
}



const PROsLocationReports: React.FC = () => {
  const [filters, setFilters] = useState({
    brand: '',
    year: '',
    quarter: '',
    month: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [activeTab, setActiveTab] = useState('overview');
  const [showRemovedDetailsModal, setShowRemovedDetailsModal] = useState(false);
  const [selectedMonthData, setSelectedMonthData] = useState<MonthlySummary | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  // Mock data for PROs Location Reports
  const locationRecords: LocationRecord[] = [
    // December 2022
    {
      id: 'LOC-000',
      brand: 'Harbor Foods',
      address: '123 Main Street, Houston, TX 77001',
      startDate: '2022-12-31',
      endDate: '2023-03-02',
      isActive: false,
      quarterStartTotal: 0,
      monthEndTotal: 1,
      quarter: 'Q4',
      month: 'December',
      year: '2022',
      createdAt: '2022-12-31',
      updatedAt: '2023-03-02'
    },
    // Q1 2023
    {
      id: 'LOC-002',
      brand: 'Harbor Foods',
      address: '456 Oak Avenue, Austin, TX 73301',
      startDate: '2023-01-15',
      isActive: true,
      quarterStartTotal: 12,
      monthEndTotal: 13,
      quarter: 'Q1',
      month: 'January',
      year: '2023',
      createdAt: '2023-01-15',
      updatedAt: '2023-01-15'
    },
    {
      id: 'LOC-003',
      brand: 'Northstar Retail',
      address: '789 Pine Street, Dallas, TX 75201',
      startDate: '2023-01-25',
      isActive: true,
      quarterStartTotal: 12,
      monthEndTotal: 13,
      quarter: 'Q1',
      month: 'January',
      year: '2023',
      createdAt: '2023-01-25',
      updatedAt: '2023-01-25'
    },
    // February 2023
    {
      id: 'LOC-004',
      brand: 'Sunrise Donuts\' Donuts',
      address: '321 Elm Street, San Antonio, TX 78201',
      startDate: '2023-02-25',
      isActive: true,
      quarterStartTotal: 13,
      monthEndTotal: 15,
      quarter: 'Q1',
      month: 'February',
      year: '2023',
      createdAt: '2023-02-25',
      updatedAt: '2023-02-25'
    },
    {
      id: 'LOC-005',
      brand: 'Sunrise Donuts\' Donuts',
      address: '654 Maple Avenue, Fort Worth, TX 76101',
      startDate: '2023-02-28',
      isActive: true,
      quarterStartTotal: 13,
      monthEndTotal: 15,
      quarter: 'Q1',
      month: 'February',
      year: '2023',
      createdAt: '2023-02-28',
      updatedAt: '2023-02-28'
    },
    // March 2023
    {
      id: 'LOC-005A',
      brand: 'Sunrise Donuts\' Donuts',
      address: '555 Pine Street, El Paso, TX 79901',
      startDate: '2023-03-31',
      endDate: '2023-05-25',
      isActive: false,
      quarterStartTotal: 15,
      monthEndTotal: 6,
      removedInMonth: '2023-05',
      quarter: 'Q1',
      month: 'March',
      year: '2023',
      createdAt: '2023-03-31',
      updatedAt: '2023-05-25'
    },
    {
      id: 'LOC-005B',
      brand: 'Acme Coffee',
      address: '888 Oak Avenue, San Antonio, TX 78201',
      startDate: '2023-03-14',
      isActive: true,
      quarterStartTotal: 15,
      monthEndTotal: 8,
      quarter: 'Q1',
      month: 'March',
      year: '2023',
      createdAt: '2023-03-14',
      updatedAt: '2023-03-14'
    },
    {
      id: 'LOC-005C',
      brand: 'Bluebird Diner',
      address: '123 Oak Street, Austin, TX 73301',
      startDate: '2023-03-24',
      isActive: true,
      quarterStartTotal: 15,
      monthEndTotal: 8,
      quarter: 'Q1',
      month: 'March',
      year: '2023',
      createdAt: '2023-03-24',
      updatedAt: '2023-03-24'
    },
    // Q2 2023
    {
      id: 'LOC-006',
      brand: 'Sunrise Donuts\' Donuts',
      address: '987 Cedar Lane, El Paso, TX 79901',
      startDate: '2023-03-31',
      endDate: '2023-05-25',
      isActive: false,
      quarterStartTotal: 15,
      monthEndTotal: 8,
      removedInMonth: '2023-05',
      quarter: 'Q1',
      month: 'March',
      year: '2023',
      createdAt: '2023-03-31',
      updatedAt: '2023-05-25'
    },
    {
      id: 'LOC-007',
      brand: 'Acme Coffee',
      address: '147 Birch Road, Arlington, TX 76001',
      startDate: '2023-04-07',
      isActive: true,
      quarterStartTotal: 18,
      monthEndTotal: 21,
      quarter: 'Q2',
      month: 'April',
      year: '2023',
      createdAt: '2023-04-07',
      updatedAt: '2023-04-07'
    },
    // Q3 2023
    {
      id: 'LOC-008',
      brand: 'Bluebird Diner',
      address: '258 Spruce Street, Corpus Christi, TX 78401',
      startDate: '2023-07-04',
      isActive: true,
      quarterStartTotal: 28,
      monthEndTotal: 30,
      quarter: 'Q3',
      month: 'July',
      year: '2023',
      createdAt: '2023-07-04',
      updatedAt: '2023-07-04'
    },
    {
      id: 'LOC-009',
      brand: 'Northstar Retail',
      address: '369 Willow Avenue, Lubbock, TX 79401',
      startDate: '2023-07-31',
      isActive: true,
      quarterStartTotal: 28,
      monthEndTotal: 36,
      quarter: 'Q3',
      month: 'July',
      year: '2023',
      createdAt: '2023-07-31',
      updatedAt: '2023-07-31'
    },
    // Q4 2023
    {
      id: 'LOC-010',
      brand: 'Fiesta Grill',
      address: '741 Oak Street, Amarillo, TX 79101',
      startDate: '2023-09-30',
      isActive: true,
      quarterStartTotal: 44,
      monthEndTotal: 44,
      quarter: 'Q4',
      month: 'September',
      year: '2023',
      createdAt: '2023-09-30',
      updatedAt: '2023-09-30'
    }
  ];

  // Generate summaries
  const generateMonthlySummaries = (): MonthlySummary[] => {
    const summaries: { [key: string]: MonthlySummary } = {};
    
    locationRecords.forEach(record => {
      const key = `${record.year}-${record.month}`;
      if (!summaries[key]) {
        summaries[key] = {
          month: record.month,
          year: record.year,
          quarter: record.quarter,
          totalLocations: 0,
          removedLocations: 0,
          removedLocationDetails: []
        };
      }
      
      summaries[key].totalLocations = Math.max(summaries[key].totalLocations, record.monthEndTotal);
      
      if (record.removedInMonth) {
        summaries[key].removedLocations++;
        summaries[key].removedLocationDetails.push(record);
      }
    });
    
    return Object.values(summaries).sort((a, b) => 
      new Date(`${a.year}-${a.month}-01`).getTime() - new Date(`${b.year}-${b.month}-01`).getTime()
    );
  };

  const monthlySummaries = generateMonthlySummaries();

  // Filter records based on search and filters
  const filteredRecords = locationRecords.filter(record => {
    const matchesSearch = searchTerm === '' || 
      record.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.address.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesBrand = filters.brand === '' || record.brand === filters.brand;
    const matchesYear = filters.year === '' || record.year === filters.year;
    const matchesQuarter = filters.quarter === '' || record.quarter === filters.quarter;
    const matchesMonth = filters.month === '' || record.month === filters.month;
    const matchesStatus = filters.status === '' || 
      (filters.status === 'active' && record.isActive) ||
      (filters.status === 'inactive' && !record.isActive);
    
    return matchesSearch && matchesBrand && matchesYear && matchesQuarter && matchesMonth && matchesStatus;
  });

  // Create records with quarterly and monthly markers for chronological display
  const createRecordsWithMarkers = () => {
    const recordsWithMarkers: Array<LocationRecord | { isMarker: true; month: string; year: string; quarter: string; isQuarterMarker: boolean }> = [];
    let currentYear = '';
    let currentQuarter = '';

    // Get all unique months from both start dates and end dates
    const allMonths = new Set<string>();
    
    filteredRecords.forEach(record => {
      // Add start date month
      const startDate = new Date(record.startDate);
      const startMonth = startDate.toLocaleDateString('en-US', { month: 'long' });
      const startYear = startDate.getFullYear().toString();
      allMonths.add(`${startMonth}-${startYear}`);
      
      // Add end date month if it exists
      if (record.endDate) {
        const endDate = new Date(record.endDate);
        const endMonth = endDate.toLocaleDateString('en-US', { month: 'long' });
        const endYear = endDate.getFullYear().toString();
        allMonths.add(`${endMonth}-${endYear}`);
      }
    });

    // Sort all months chronologically
    const sortedMonths = Array.from(allMonths).sort((a, b) => {
      const [monthA, yearA] = a.split('-');
      const [monthB, yearB] = b.split('-');
      const monthToNumber: { [key: string]: number } = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
        'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
      };
      const dateA = new Date(parseInt(yearA), monthToNumber[monthA] - 1, 1);
      const dateB = new Date(parseInt(yearB), monthToNumber[monthB] - 1, 1);
      return dateA.getTime() - dateB.getTime();
    });

    // Sort records by start date to maintain chronological order
    const sortedRecords = [...filteredRecords].sort((a, b) => 
      new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    // Add markers and records for each month
    sortedMonths.forEach(monthKey => {
      const [month, year] = monthKey.split('-');
      
      // Determine quarter
      const monthToNumber: { [key: string]: number } = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
        'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
      };
      const monthNum = monthToNumber[month];
      let quarter = '';
      if (monthNum >= 1 && monthNum <= 3) quarter = 'Q1';
      else if (monthNum >= 4 && monthNum <= 6) quarter = 'Q2';
      else if (monthNum >= 7 && monthNum <= 9) quarter = 'Q3';
      else if (monthNum >= 10 && monthNum <= 12) quarter = 'Q4';
      
      // Add quarter marker row if quarter/year changes
      if (quarter !== currentQuarter || year !== currentYear) {
        recordsWithMarkers.push({
          isMarker: true,
          isQuarterMarker: true,
          month: month,
          year: year,
          quarter: quarter
        });
        currentQuarter = quarter;
        currentYear = year;
      }
      
      // Add month marker row
      recordsWithMarkers.push({
        isMarker: true,
        isQuarterMarker: false,
        month: month,
        year: year,
        quarter: quarter
      });
      
      // Add records that started in this month
      sortedRecords.forEach(record => {
        const startDate = new Date(record.startDate);
        const recordMonth = startDate.toLocaleDateString('en-US', { month: 'long' });
        const recordYear = startDate.getFullYear().toString();
        
        if (recordMonth === month && recordYear === year) {
          recordsWithMarkers.push(record);
        }
      });
    });

    return recordsWithMarkers;
  };

  const recordsWithMarkers = createRecordsWithMarkers();

  const handleShowRemovedDetails = (monthData: MonthlySummary) => {
    setSelectedMonthData(monthData);
    setShowRemovedDetailsModal(true);
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="secondary">Inactive</Badge>;
  };

  const formatDate = (dateString: string) => {
    // Parse the date string to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getUniqueBrands = () => {
    return Array.from(new Set(locationRecords.map(record => record.brand)));
  };

  const getUniqueYears = () => {
    return Array.from(new Set(locationRecords.map(record => record.year))).sort();
  };

  const getUniqueQuarters = () => {
    return Array.from(new Set(locationRecords.map(record => record.quarter))).sort();
  };

  const getQuarterStartTotal = (month: string, year: string) => {
    // Use the beginning-of-month total (same as previous month's end total)
    return getBeginningOfMonthTotal(month, year);
  };

  const getMonthEndTotal = (month: string, year: string) => {
    // Use the rolling monthly total for the end of the month
    return getMonthlyTotal(month, year);
  };

  const isFirstMonthOfQuarter = (month: string, quarter: string) => {
    const monthToNumber: { [key: string]: number } = {
      'January': 1, 'February': 2, 'March': 3,
      'April': 4, 'May': 5, 'June': 6,
      'July': 7, 'August': 8, 'September': 9,
      'October': 10, 'November': 11, 'December': 12
    };
    
    const monthNum = monthToNumber[month];
    
    // First month of each quarter: January (1), April (4), July (7), October (10)
    const firstMonthsOfQuarters = [1, 4, 7, 10];
    
    return firstMonthsOfQuarters.includes(monthNum);
  };

  const getLastDayOfMonth = (month: string, year: string) => {
    const monthToNumber: { [key: string]: number } = {
      'January': 0, 'February': 1, 'March': 2,
      'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8,
      'October': 9, 'November': 10, 'December': 11
    };
    
    const monthNum = monthToNumber[month];
    const yearNum = parseInt(year);
    const lastDayOfMonth = new Date(yearNum, monthNum + 1, 0);
    return lastDayOfMonth.getDate();
  };

  const getRemovedLocationsInMonth = (month: string, year: string) => {
    const monthToNumber: { [key: string]: number } = {
      'January': 0, 'February': 1, 'March': 2,
      'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8,
      'October': 9, 'November': 10, 'December': 11
    };
    
    const monthNum = monthToNumber[month];
    const yearNum = parseInt(year);
    const firstDayOfMonth = new Date(yearNum, monthNum, 1);
    const lastDayOfMonth = new Date(yearNum, monthNum + 1, 0);
    
    // Find locations that have an end date within this month
    return locationRecords.filter(record => {
      if (!record.endDate) return false;
      const endDate = new Date(record.endDate);
      return endDate >= firstDayOfMonth && endDate <= lastDayOfMonth;
    });
  };

  // Calculate monthly totals using rolling month-to-month approach
  const calculateMonthlyTotals = () => {
    const monthlyTotals: { [key: string]: number } = {};
    let runningTotal = 0;
    
    // Get all unique months from both start dates and end dates, sorted chronologically
    const allMonths = new Set<string>();
    
    locationRecords.forEach(record => {
      // Add start date month
      const startDate = new Date(record.startDate);
      const startMonth = startDate.toLocaleDateString('en-US', { month: 'long' });
      const startYear = startDate.getFullYear().toString();
      allMonths.add(`${startMonth}-${startYear}`);
      
      // Add end date month if it exists
      if (record.endDate) {
        const endDate = new Date(record.endDate);
        const endMonth = endDate.toLocaleDateString('en-US', { month: 'long' });
        const endYear = endDate.getFullYear().toString();
        allMonths.add(`${endMonth}-${endYear}`);
      }
    });

    // Sort all months chronologically
    const sortedMonths = Array.from(allMonths).sort((a, b) => {
      const [monthA, yearA] = a.split('-');
      const [monthB, yearB] = b.split('-');
      const monthToNumber: { [key: string]: number } = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
        'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
      };
      const dateA = new Date(parseInt(yearA), monthToNumber[monthA] - 1, 1);
      const dateB = new Date(parseInt(yearB), monthToNumber[monthB] - 1, 1);
      return dateA.getTime() - dateB.getTime();
    });

    console.log('Sorted months:', sortedMonths);

    // Calculate running totals month by month
    sortedMonths.forEach(monthKey => {
      const [month, year] = monthKey.split('-');
      
      // Count locations that started in this month
      const startedThisMonth = locationRecords.filter(record => {
        const startDate = new Date(record.startDate);
        const recordMonth = startDate.toLocaleDateString('en-US', { month: 'long' });
        const recordYear = startDate.getFullYear().toString();
        return recordMonth === month && recordYear === year;
      }).length;
      
      // Count locations that were removed in this month
      const removedThisMonth = locationRecords.filter(record => {
        if (!record.endDate) return false;
        const endDate = new Date(record.endDate);
        const recordMonth = endDate.toLocaleDateString('en-US', { month: 'long' });
        const recordYear = endDate.getFullYear().toString();
        return recordMonth === month && recordYear === year;
      }).length;
      
      // Update running total
      runningTotal = runningTotal + startedThisMonth - removedThisMonth;
      monthlyTotals[monthKey] = runningTotal;
      
      console.log(`${month} ${year}: +${startedThisMonth} -${removedThisMonth} = ${runningTotal} total`);
    });

    return monthlyTotals;
  };

  const getMonthlyTotal = (month: string, year: string) => {
    const monthlyTotals = calculateMonthlyTotals();
    const monthKey = `${month}-${year}`;
    const total = monthlyTotals[monthKey] || 0;
    
    // Debug log for April
    if (month === 'April' && year === '2023') {
      console.log(`April 2023 total: ${total}`);
      console.log('All monthly totals:', monthlyTotals);
    }
    
    return total;
  };

  const getBeginningOfMonthTotal = (month: string, year: string) => {
    // For quarter start totals, we need the total at the beginning of the month
    // This should be the same as the previous month's end total
    
    const monthToNumber: { [key: string]: number } = {
      'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
      'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
    };
    
    const monthNum = monthToNumber[month];
    const yearNum = parseInt(year);
    
    // If it's January, go back to December of previous year
    if (monthNum === 1) {
      return getMonthlyTotal('December', (yearNum - 1).toString());
    } else {
      const previousMonthNum = monthNum - 1;
      const previousMonth = Object.keys(monthToNumber).find(key => monthToNumber[key] === previousMonthNum);
      
      // Try to get the previous month's total
      let previousTotal = getMonthlyTotal(previousMonth!, year);
      
      // If previous month has no data, keep going back until we find a month with data
      if (previousTotal === 0 && monthNum > 1) {
        // Go back to the last month that has data
        for (let i = monthNum - 2; i >= 1; i--) {
          const earlierMonth = Object.keys(monthToNumber).find(key => monthToNumber[key] === i);
          previousTotal = getMonthlyTotal(earlierMonth!, year);
          if (previousTotal > 0) break;
        }
      }
      
      return previousTotal;
    }
  };

  const toggleMonthExpansion = (month: string, year: string) => {
    const monthKey = `${month}-${year}`;
    const newExpandedMonths = new Set(expandedMonths);
    
    if (newExpandedMonths.has(monthKey)) {
      newExpandedMonths.delete(monthKey);
    } else {
      newExpandedMonths.add(monthKey);
    }
    
    setExpandedMonths(newExpandedMonths);
  };

  const isMonthExpanded = (month: string, year: string) => {
    return expandedMonths.has(`${month}-${year}`);
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
            <PageTitle title="PROs Location Reports" />
            <Button 
              onClick={() => {
                setFilters({
                  brand: '',
                  year: '',
                  quarter: '',
                  month: '',
                  status: '',
                  dateFrom: '',
                  dateTo: ''
                });
                setSearchTerm('');
              }}
              style={{ 
                background: 'white', 
                color: '#333', 
                border: '1px solid #e0e0e0', 
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500'
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>
          
          {/* Filters Section */}
          <div className={styles.filterSection}>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Brand</Form.Label>
                  <Form.Select
                    value={filters.brand}
                    onChange={(e) => setFilters(prev => ({ ...prev, brand: e.target.value }))}
                  >
                    <option value="">Any brand</option>
                    {getUniqueBrands().map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Year</Form.Label>
                  <Form.Select
                    value={filters.year}
                    onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                  >
                    <option value="">Any year</option>
                    {getUniqueYears().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Quarter</Form.Label>
                  <Form.Select
                    value={filters.quarter}
                    onChange={(e) => setFilters(prev => ({ ...prev, quarter: e.target.value }))}
                  >
                    <option value="">Any quarter</option>
                    {getUniqueQuarters().map(quarter => (
                      <option key={quarter} value={quarter}>{quarter}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="">Any status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Filter by Date Range</Form.Label>
                  <DateRangePicker
                    onChange={(dates) => {
                      if (dates && dates.length === 2) {
                        setFilters(prev => ({
                          ...prev,
                          dateFrom: dates[0]?.format('YYYY-MM-DD') || '',
                          dateTo: dates[1]?.format('YYYY-MM-DD') || ''
                        }));
                      } else {
                        setFilters(prev => ({
                          ...prev,
                          dateFrom: '',
                          dateTo: ''
                        }));
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Control
                    type="text"
                    placeholder="Search by brand or address..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Tabs for different views */}
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')}>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="overview">Overview</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="detailed">Detailed Records</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="monthly">Monthly Summary</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="quarterly">Quarterly Summary</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              {/* Overview Tab */}
              <Tab.Pane eventKey="overview">
                <div className={styles.overviewGrid}>
                  <Card className={styles.summaryCard}>
                    <Card.Header>
                      <h6>Total Active Locations</h6>
                    </Card.Header>
                    <Card.Body>
                      <h3>{locationRecords.filter(r => r.isActive).length}</h3>
                      <small className="text-muted">Currently providing services</small>
                    </Card.Body>
                  </Card>
                  
                  <Card className={styles.summaryCard}>
                    <Card.Header>
                      <h6>Total Historical Locations</h6>
                    </Card.Header>
                    <Card.Body>
                      <h3>{locationRecords.length}</h3>
                      <small className="text-muted">All time including inactive</small>
                    </Card.Body>
                  </Card>
                  
                  <Card className={styles.summaryCard}>
                    <Card.Header>
                      <h6>Active Brands</h6>
                    </Card.Header>
                    <Card.Body>
                      <h3>{new Set(locationRecords.filter(r => r.isActive).map(r => r.brand)).size}</h3>
                      <small className="text-muted">Brands with active locations</small>
                    </Card.Body>
                  </Card>
                  
                  <Card className={styles.summaryCard}>
                    <Card.Header>
                      <h6>Recent Activity</h6>
                    </Card.Header>
                    <Card.Body>
                      <div className={styles.recentActivity}>
                        {locationRecords
                          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
                          .slice(0, 5)
                          .map(record => (
                            <div key={record.id} className={styles.activityItem}>
                              <span className={styles.brandName}>{record.brand}</span>
                              <span className={styles.activityDate}>{formatDate(record.updatedAt)}</span>
                              {getStatusBadge(record.isActive)}
                            </div>
                          ))}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Tab.Pane>

              {/* Detailed Records Tab */}
              <Tab.Pane eventKey="detailed">
                <Table hover responsive className="legacy-row-height-table">
                  <thead>
                    <tr>
                      <th>Brand</th>
                      <th>Address</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Locations: 1st of the Quarter</th>
                      <th>Locations: End of the Month</th>
                      <th>Locations: Removed Monthly</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recordsWithMarkers.map((item, index) => {
                      const rows: React.ReactElement[] = [];
                      
                      // Check if this is a marker row
                      if ('isMarker' in item && item.isMarker) {
                        if (item.isQuarterMarker) {
                          rows.push(
                            <tr key={`quarter-marker-${item.quarter}-${item.year}`} className={styles.quarterMarkerRow}>
                              <td colSpan={8} className={styles.quarterMarkerCell}>
                                <strong>{item.quarter} {item.year}</strong>
                              </td>
                            </tr>
                          );
                        } else {
                          // Check if this is the first month of the quarter
                          const isFirstMonth = isFirstMonthOfQuarter(item.month, item.quarter);
                          
                          rows.push(
                            <tr key={`month-marker-${item.month}-${item.year}`} className={styles.markerRow}>
                              <td className={styles.markerCell}>
                                <strong>{item.month} {item.year}</strong>
                              </td>
                              <td className={styles.markerCell}>
                              </td>
                              <td className={styles.markerCell}>
                              </td>
                              <td className={styles.markerCell}>
                              </td>
                              <td className={styles.markerCell}>
                                {isFirstMonth && (
                                  <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                    Q{item.quarter.slice(1)}: As of {item.month.slice(0, 3)} 1 {item.year} ({getQuarterStartTotal(item.month, item.year)} Total)
                                  </span>
                                )}
                              </td>
                              <td className={styles.markerCell}>
                                <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                  As of {item.month.slice(0, 3)} {getLastDayOfMonth(item.month, item.year)} {item.year} ({getMonthEndTotal(item.month, item.year)} total)
                                </span>
                              </td>
                              <td className={styles.markerCell}>
                                {(() => {
                                  const removedLocations = getRemovedLocationsInMonth(item.month, item.year);
                                  if (removedLocations.length > 0) {
                                    return (
                                      <span 
                                        style={{ 
                                          fontSize: '12px', 
                                          fontWeight: 'bold',
                                          cursor: 'pointer',
                                          color: '#007bff',
                                          textDecoration: 'underline'
                                        }}
                                        onClick={() => toggleMonthExpansion(item.month, item.year)}
                                        title="Click to view removed location details"
                                      >
                                        Minus -{removedLocations.length} Location{removedLocations.length > 1 ? 's' : ''}
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </td>
                              <td className={styles.markerCell}>
                              </td>
                            </tr>
                          );
                          
                          // Add expandable details rows for removed locations
                          if (isMonthExpanded(item.month, item.year)) {
                            const removedLocations = getRemovedLocationsInMonth(item.month, item.year);
                            removedLocations.forEach((location, locationIndex) => {
                              rows.push(
                                <tr key={`removed-detail-${item.month}-${item.year}-${locationIndex}`} className={styles.removedDetailRow}>
                                  <td style={{ paddingLeft: '40px', fontSize: '11px', color: '#666' }}>
                                    {location.brand}
                                  </td>
                                  <td style={{ fontSize: '11px', color: '#666' }}>
                                    {location.address}
                                  </td>
                                  <td style={{ fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>
                                    {formatDate(location.startDate)}
                                  </td>
                                  <td style={{ fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>
                                    {location.endDate ? formatDate(location.endDate) : '-'}
                                  </td>
                                  <td></td>
                                  <td></td>
                                  <td style={{ fontSize: '11px', color: '#666' }}>
                                    Removed: {location.endDate ? formatDate(location.endDate) : 'Unknown'}
                                  </td>
                                  <td style={{ fontSize: '11px', color: '#666' }}>
                                    <Badge bg="secondary" style={{ fontSize: '10px' }}>Removed</Badge>
                                  </td>
                                </tr>
                              );
                            });
                          }
                        }
                      } else {
                        // Regular record row
                        const record = item as LocationRecord;
                        rows.push(
                          <tr key={record.id}>
                            <td>{record.brand}</td>
                            <td>{record.address}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>{formatDate(record.startDate)}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>{record.endDate ? formatDate(record.endDate) : '-'}</td>
                            <td></td>
                            <td>1</td>
                            <td>-</td>
                            <td>{getStatusBadge(record.isActive)}</td>
                          </tr>
                        );
                      }
                      
                      return rows;
                    }).flat()}
                  </tbody>
                </Table>
              </Tab.Pane>

              {/* Monthly Summary Tab */}
              <Tab.Pane eventKey="monthly">
                <Table hover responsive className="legacy-row-height-table">
                  <thead>
                    <tr>
                      <th>Month/Year</th>
                      <th>Quarter</th>
                      <th>Total Locations</th>
                      <th>Removed Locations</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlySummaries.map(summary => (
                      <tr key={`${summary.year}-${summary.month}`}>
                        <td>{summary.month} {summary.year}</td>
                        <td>{summary.quarter}</td>
                        <td>{summary.totalLocations}</td>
                        <td>
                          {summary.removedLocations > 0 ? (
                            <div>
                              <Badge bg="danger">-{summary.removedLocations} Location{summary.removedLocations > 1 ? 's' : ''}</Badge>
                              <Button 
                                variant="link" 
                                size="sm" 
                                className={styles.detailsLink}
                                onClick={() => handleShowRemovedDetails(summary)}
                              >
                                View Details
                              </Button>
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => setActiveTab('detailed')}
                          >
                            View Records
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Tab.Pane>

              {/* Quarterly Summary Tab */}
              <Tab.Pane eventKey="quarterly">
                <div className={styles.quarterlyGrid}>
                  {['Q1', 'Q2', 'Q3', 'Q4'].map(quarter => {
                    const quarterData = monthlySummaries.filter(s => s.quarter === quarter);
                    const totalLocations = quarterData.length > 0 ? Math.max(...quarterData.map(s => s.totalLocations)) : 0;
                    const totalRemoved = quarterData.reduce((sum, s) => sum + s.removedLocations, 0);
                    
                    return (
                      <Card key={quarter} className={styles.quarterCard}>
                        <Card.Header>
                          <h6>{quarter} Summary</h6>
                        </Card.Header>
                        <Card.Body>
                          <div className={styles.quarterStats}>
                            <div>
                              <strong>Total Locations:</strong> {totalLocations}
                            </div>
                            <div>
                              <strong>Removed:</strong> {totalRemoved > 0 ? `-${totalRemoved}` : '0'}
                            </div>
                            <div>
                              <strong>Months:</strong> {quarterData.length}
                            </div>
                          </div>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="mt-2"
                            onClick={() => setActiveTab('monthly')}
                          >
                            View Monthly Breakdown
                          </Button>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      {/* Removed Locations Details Modal */}
      <Modal 
        show={showRemovedDetailsModal} 
        onHide={() => setShowRemovedDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Removed Locations - {selectedMonthData?.month} {selectedMonthData?.year}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMonthData && (
            <div>
              <p className="mb-3">
                <strong>Total Removed:</strong> {selectedMonthData.removedLocations} location{selectedMonthData.removedLocations > 1 ? 's' : ''}
              </p>
              <Table hover responsive>
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th>Address</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMonthData.removedLocationDetails.map(location => (
                    <tr key={location.id}>
                      <td>{location.brand}</td>
                      <td>{location.address}</td>
                      <td>{formatDate(location.startDate)}</td>
                      <td>{location.endDate ? formatDate(location.endDate) : '-'}</td>
                      <td>{location.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(PROsLocationReports);
